/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';

export const URGENCY = ['overdue', 'this_week', 'this_month', 'later'] as const;
export type Urgency = (typeof URGENCY)[number];

/** How each band reads on screen. Ordered by how soon it needs somebody. */
export const URGENCY_LABEL: Record<string, string> = {
  overdue: 'Overdue',
  this_week: 'This week',
  // Within the one expiring window the dashboard counts (`DL-04`): 30 days.
  this_month: 'Within 30 days',
  later: 'Later',
  done: 'Done',
};

export const deadlineSchema = z.object({
  id: z.string(),
  kind: z.enum(['obligation', 'expiry']),
  title: z.string(),
  agreement_id: z.string(),
  agreement_reference: z.string(),
  due_date: z.string().nullable(),
  owner_name: z.string().nullable(),
  completed: z.boolean(),
  urgency: z.string(),
  /**
   * For an obligation: whether the reader may tick it off here. Always false
   * for an expiry. A server that predates it does not send it, and the answer
   * then is "may not" -- the button is simply not offered.
   */
  can_complete: z.boolean().default(false),
});

export type Deadline = z.infer<typeof deadlineSchema>;

/**
 * Everything with a date; or, with `expiring`, only the signed agreements
 * expiring within the one window (`DL-04`) -- what the dashboard's *Expiring
 * soon* counts, so the list it opens holds the same agreements.
 */
export const deadlinesQuery = ({ expiring = false }: { expiring?: boolean } = {}) =>
  queryOptions({
    queryKey: expiring ? (['deadlines', 'expiring'] as const) : (['deadlines'] as const),
    queryFn: ({ signal }) =>
      request(
        expiring ? '/api/v1/deadlines?expiring=true' : '/api/v1/deadlines',
        z.array(deadlineSchema),
        { signal },
      ),
  });
